import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import apiClient from '../../../server/server';
import { getAuthToken } from '@/utils/authStorage';

const baseURL = apiClient.defaults.baseURL;

export const articlesScraperApiSlice = createApi({
  reducerPath: 'articlesScraperApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers) => {
      const token = getAuthToken();
      if (token) headers.set('authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Source', 'Draft', 'Magazine', 'Article'],
  endpoints: (builder) => ({
    // ===== Sources CRUD =====
    listSources: builder.query({
      query: () => '/admin/articles-scraper/sources',
      providesTags: ['Source'],
    }),
    getSource: builder.query({
      query: (id) => `/admin/articles-scraper/sources/${id}`,
      providesTags: (r, e, id) => [{ type: 'Source', id }],
    }),
    createSource: builder.mutation({
      query: (body) => ({
        url: '/admin/articles-scraper/sources',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Source', 'Magazine'],
    }),
    updateSource: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/articles-scraper/sources/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Source', 'Magazine'],
    }),
    deleteSource: builder.mutation({
      query: (id) => ({
        url: `/admin/articles-scraper/sources/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Source', 'Magazine'],
    }),

    // ===== Magazines (MG profiles + their source status) =====
    listMagazines: builder.query({
      query: () => '/admin/articles-scraper/magazines',
      providesTags: ['Magazine'],
    }),

    // ===== Feed discovery =====
    discoverFeed: builder.mutation({
      query: (hostname) => ({
        url: '/admin/articles-scraper/discover-feed',
        method: 'POST',
        body: { hostname },
      }),
    }),

    // ===== Ingestion =====
    scrapeUrl: builder.mutation({
      query: (body) => ({
        url: '/admin/articles-scraper/scrape-url',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Draft', 'Article'],
    }),
    scrapeFeed: builder.mutation({
      query: (body) => ({
        url: '/admin/articles-scraper/scrape-feed',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Draft', 'Article'],
    }),
    scrapeListing: builder.mutation({
      query: (body) => ({
        url: '/admin/articles-scraper/scrape-listing',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Draft', 'Article'],
    }),

    // ===== Drafts review =====
    listDrafts: builder.query({
      query: ({ skip = 0, limit = 20 } = {}) =>
        `/admin/articles-scraper/drafts?skip=${skip}&limit=${limit}`,
      providesTags: ['Draft'],
    }),
    approveDraft: builder.mutation({
      query: (id) => ({
        url: `/admin/articles-scraper/${id}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Draft', 'Article'],
    }),
    rejectDraft: builder.mutation({
      query: (id) => ({
        url: `/admin/articles-scraper/${id}/reject`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Draft', 'Article'],
    }),

    // ===== Article fetch (for preview) =====
    getArticle: builder.query({
      query: (id) => `/articles/get/${id}`,
      providesTags: (r, e, id) => [{ type: 'Article', id }],
    }),

    // ===== Approved/published articles (browsable feed) =====
    listAllArticles: builder.query({
      query: ({ page = 1, limit = 20, search, sourceName } = {}) => {
        const params = new URLSearchParams();
        params.set('pagination', 'true');
        params.set('isHidden', 'false');
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (search) params.set('search', search);
        if (sourceName) params.set('sourceName', sourceName);
        return `/articles/all?${params.toString()}`;
      },
      providesTags: ['Article'],
    }),

    // ===== Test extraction without persisting =====
    testExtract: builder.mutation({
      query: (body) => ({
        url: '/admin/articles-scraper/test-extract',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useListSourcesQuery,
  useGetSourceQuery,
  useCreateSourceMutation,
  useUpdateSourceMutation,
  useDeleteSourceMutation,
  useListMagazinesQuery,
  useDiscoverFeedMutation,
  useScrapeUrlMutation,
  useScrapeFeedMutation,
  useScrapeListingMutation,
  useListDraftsQuery,
  useApproveDraftMutation,
  useRejectDraftMutation,
  useGetArticleQuery,
  useTestExtractMutation,
  useListAllArticlesQuery,
} = articlesScraperApiSlice;
